import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CustomerDocument,
  DocType,
  documentsApi,
} from '../lib/documents-api';

interface UseCustomerDocumentsResult {
  documents: CustomerDocument[];
  total: number;
  /** True when the results came from the trigram fallback rather than full-text. */
  fuzzy: boolean;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Fetch one customer's documents, with debounced search.
 *
 * Requests are sequenced rather than cancelled: every fetch stamps a monotonic id and a
 * late response with a stale id is discarded. Typing "contract" fires several overlapping
 * requests, and without this the slowest one wins and the list contradicts the search box.
 */
export function useCustomerDocuments(
  accountId: string | undefined,
  filters: { q?: string; docType?: DocType; includeArchived?: boolean },
): UseCustomerDocumentsResult {
  const [documents, setDocuments] = useState<CustomerDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [fuzzy, setFuzzy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);

  const { q, docType, includeArchived } = filters;

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!accountId) return;
    const id = ++seq.current;
    // Debounce only the text query; a filter chip should feel instant.
    const delay = q ? 250 : 0;
    setLoading(true);
    const timer = setTimeout(() => {
      documentsApi
        .list({ accountId, q, docType, includeArchived })
        .then((page) => {
          if (id !== seq.current) return; // a newer request is already in flight
          setDocuments(page.items);
          setTotal(page.total);
          setFuzzy(page.fuzzy === true);
          setError(null);
        })
        .catch((e: unknown) => {
          if (id !== seq.current) return;
          setError((e as Error)?.message ?? 'Failed to load documents');
          setDocuments([]);
          setTotal(0);
          setFuzzy(false);
        })
        .finally(() => {
          if (id === seq.current) setLoading(false);
        });
    }, delay);
    return () => clearTimeout(timer);
  }, [accountId, q, docType, includeArchived, nonce]);

  return { documents, total, fuzzy, loading, error, reload };
}
